import React from 'react';
import { Activity, Flame, Medal, Target, Star, Users, Info } from 'lucide-react';
import { Tooltip } from 'antd';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';

// One-line stat strip — soft tinted icon chip + fixed two-line text rhythm so
// every item shares the same baseline (label 11/12, value 15/18). Items are
// built as a list and interleaved with uniform hairline dividers.
const StatItem = ({ t, icon, label, value, valueColor, accent, onInfo, infoTitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: t.s[3], minWidth: 0 }}>
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: t.r.sm, flexShrink: 0,
        background: `${accent}1F`, color: accent,
      }}
    >
      {icon}
    </span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 11, lineHeight: '12px', fontWeight: t.font.weight.medium,
          color: t.c.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {onInfo && (
          <Tooltip title={infoTitle}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onInfo(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', border: 'none',
                background: 'transparent', color: t.c.textTertiary, cursor: 'pointer',
                padding: 0, margin: 0,
              }}
              aria-label={infoTitle}
            >
              <Info size={11} />
            </button>
          </Tooltip>
        )}
      </span>
      <span
        style={{
          fontSize: 15, lineHeight: '18px', fontWeight: t.font.weight.semibold,
          color: valueColor || t.c.text, whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
        }}
      >
        {value}
      </span>
    </span>
  </div>
);

const GoalsStatChips = ({
  bannerConfig = {},
  badgeIconUrl = null,
  streakIconUrl = null,
  employeeAvgScore = null,
  goalsCount = 0,
  maxGoals = 0,
  onBadgeInfoClick,
  onStreakInfoClick,
  // When embedded inside another card (e.g. the cycle-overview card) skip the
  // outer card chrome so we don't nest borders.
  bare = false,
  // Gamification: { companyAverageScore, myScore } from /team-stats
  scoreBenchmark = null,
  benchmarkMaxScore = 10,
}) => {
  const t = useDashTheme();

  const hasValidScore = employeeAvgScore != null && parseFloat(employeeAvgScore) > 0;

  const hasValidStreak = bannerConfig.currentStreak &&
    bannerConfig.currentStreak !== '0 Month' &&
    bannerConfig.currentStreak !== '0 Months' &&
    bannerConfig.currentStreak !== '0' &&
    !String(bannerConfig.currentStreak).includes('0 Month') &&
    !String(bannerConfig.currentStreak).includes('0 Months');

  const badgeLabel = hasValidScore ? (bannerConfig?.rankLabel || 'Growing Employee') : '—';
  const hasBadgeImage = badgeIconUrl && badgeIconUrl.trim() !== '';

  const iconImg = (url, alt) => (
    <img src={url} alt={alt} style={{ width: 15, height: 15, objectFit: 'cover', borderRadius: 3 }} onError={(e) => { e.target.style.display = 'none'; }} />
  );

  // Saturated chart accents read well on their own 12%-alpha tint.
  const C = { blue: t.accents[0], green: t.accents[1], purple: t.accents[2], amber: '#D97706' };

  const items = [
    {
      key: 'status',
      icon: <Activity size={15} strokeWidth={2} />,
      accent: C.blue,
      label: 'Status',
      value: hasValidScore ? (bannerConfig.rankLabel || 'On Track') : 'On Track',
    },
    {
      key: 'streak',
      icon: streakIconUrl && hasValidStreak ? iconImg(streakIconUrl, 'Streak') : <Flame size={15} strokeWidth={2} />,
      accent: C.amber,
      label: 'Monthly Streak',
      value: hasValidStreak ? bannerConfig.currentStreak : '—',
      onInfo: onStreakInfoClick,
      infoTitle: 'Streak information',
    },
    hasValidScore && {
      key: 'badge',
      icon: hasBadgeImage ? iconImg(badgeIconUrl, badgeLabel) : <Medal size={15} strokeWidth={2} />,
      accent: C.amber,
      label: 'Badge',
      value: badgeLabel,
      valueColor: C.amber,
      onInfo: onBadgeInfoClick,
      infoTitle: 'Badge information',
    },
    {
      key: 'goals',
      icon: <Target size={15} strokeWidth={2} />,
      accent: C.purple,
      label: 'Goals Added',
      value: maxGoals > 0 ? `${goalsCount} / ${maxGoals}` : `${goalsCount}`,
    },
    {
      key: 'score',
      icon: <Star size={15} strokeWidth={2} />,
      accent: C.green,
      label: 'Score',
      value: hasValidScore ? `${(Math.floor(parseFloat(employeeAvgScore) * 10) / 10).toFixed(1)} / 10` : '— / 10',
    },
  ].filter(Boolean);

  if (scoreBenchmark?.companyAverageScore != null) {
    const avg = scoreBenchmark.companyAverageScore;
    const mine = scoreBenchmark.myScore;
    const ahead = mine != null && mine >= avg;
    items.push({
      key: 'companyAvg',
      icon: <Users size={15} strokeWidth={2} />,
      accent: ahead ? C.green : C.amber,
      label: 'Company Avg',
      value: `${avg} / ${benchmarkMaxScore}`,
      valueColor: ahead ? C.green : undefined,
      onInfo: () => {},
      infoTitle: mine == null
        ? 'No score yet this cycle'
        : ahead
          ? 'Above company average — keep it up!'
          : `${(avg - mine).toFixed(1)} more to reach the company average`,
    });
  }

  const wrapperStyle = {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
    gap: `${t.s[3]}px ${t.s[5]}px`,
    ...(bare ? {} : {
      padding: `${t.s[3]}px ${t.s[4]}px`, background: t.c.surface,
      border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, boxShadow: t.shadow.sm,
    }),
  };

  return (
    <div style={wrapperStyle}>
      {items.map((item, i) => (
        <React.Fragment key={item.key}>
          {i > 0 && <span style={{ width: 1, height: 28, background: t.c.border, flexShrink: 0 }} />}
          <StatItem t={t} {...item} />
        </React.Fragment>
      ))}
    </div>
  );
};

export default GoalsStatChips;
